import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import CityDetails from '../CityDetails';
import TripHeader from '../components/TripHeader';
import CityCard from '../components/CityCard';
import PlaceCard from '../components/PlaceCard';
import TodoGrid from '../components/TodoGrid';
import LinkGrid from '../components/LinkGrid';
import { useLocalStorage, generateStorageKey } from '../utils/useLocalStorage';
import { useTrip } from '../hooks/useTrips';
import { TripOverviewSkeleton } from '../components/Skeleton';

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ─── Utility ─────────────────────────────────────────────────────────────────

function calculateTotalCost(trip) {
    return trip?.cities?.flatMap(c => c.days).flatMap(d => d.activities)
        .reduce((sum, a) => sum + (a.cost || 0), 0) ?? 0;
}

function calculateTripDays(start, end) {
    return Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
}

function groupByZone(places) {
    if (!Array.isArray(places)) return {};
    return places.reduce((acc, p) => {
        const zone = p?.zone || 'Altro';
        acc[zone] = [...(acc[zone] || []), p];
        return acc;
    }, {});
}

// ─── Zone selector sub-component ─────────────────────────────────────────────

function ZoneSelector({ grouped, selected, onSelect }) {
    const { t } = useTranslation();
    const zones = Object.keys(grouped);
    if (zones.length <= 1) return null;
    if (selected) return null;

    return (
        <div className="mb-4">
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">{t('tripOverview.zone.selectZone')}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {zones.map(zone => (
                    <div
                        key={zone}
                        onClick={() => onSelect(zone)}
                        className="card p-3 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md transition-all duration-200 text-center"
                    >
                        <p className="font-bold text-gray-900 dark:text-gray-100">{zone}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('tripOverview.zone.places', { count: grouped[zone].length })}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Places section ───────────────────────────────────────────────────────────

function PlacesSection({ places, category, title, buildLinks, selectedZone, onZoneSelect, priceFormat = 'full' }) {
    const { t } = useTranslation();
    if (!places || places.length === 0) return null;

    const grouped = groupByZone(places);
    const selected = selectedZone[category];
    const placesToShow = selected ? (grouped[selected] || places) : places;
    const showGrid = selected || Object.keys(grouped).length <= 1;

    return (
        <div>
            <h3 className="section-title mb-4">{title}</h3>

            <ZoneSelector
                grouped={grouped}
                selected={selected}
                onSelect={(zone) => onZoneSelect(category, zone)}
            />

            {selected && (
                <div className="flex items-center gap-3 mb-4">
                    <button onClick={() => onZoneSelect(category, selected)} className="btn-outline text-sm">
                        {t('tripOverview.zone.backToZones')}
                    </button>
                    <span className="font-bold text-gray-900 dark:text-gray-100">
                        {t('tripOverview.zone.zoneLabel', { name: selected })}
                    </span>
                </div>
            )}

            {showGrid && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {placesToShow.map((place, i) => (
                        <PlaceCard
                            key={i}
                            title={place.name}
                            address={place.address}
                            image={place.image}
                            rating={place.rating}
                            price={place.price}
                            specialty={place.specialty}
                            hours={place.hours}
                            notes={place.notes}
                            links={buildLinks(place)}
                            priceFormat={priceFormat}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Lazy-load wrapper ────────────────────────────────────────────────────────

export default function TripOverview({ trip: initial, token, onBack, initialCityName }) {
    const { t } = useTranslation();
    const needsFetch = !initial?.cities;
    const { trip: fetched, loading, error } = useTrip(needsFetch ? initial?.id : null, token);
    const trip = needsFetch ? fetched : initial;

    if (loading) return <TripOverviewSkeleton onBack={onBack} />;

    if (error) return (
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col gap-4">
            <button onClick={onBack} className="btn-outline self-start text-sm">{t('tripOverview.backToTrips')}</button>
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
                {error}
            </div>
        </div>
    );

    if (!trip) return null;
    return <TripOverviewContent trip={trip} token={token} onBack={onBack} initialCityName={initialCityName} />;
}

// ─── Main component ───────────────────────────────────────────────────────────

function TripOverviewContent({ trip, token, onBack, initialCityName }) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'en' ? 'en-US' : 'it-IT';

    const TABS = [
        { id: 'timeline',  label: t('tripOverview.tabs.timeline') },
        { id: 'details',   label: t('tripOverview.tabs.details') },
        { id: 'map',       label: t('tripOverview.tabs.map') },
        { id: 'checklist', label: t('tripOverview.tabs.checklist') },
        { id: 'links',     label: t('tripOverview.tabs.links') },
    ];

    const [activeTab, setActiveTab]       = useState('timeline');
    const [selectedCity, setSelectedCity] = useState(null);
    const cityInitialized = useRef(false);

    // Auto-seleziona città da URL (es. refresh su /trip/1/city/Tokyo)
    useEffect(() => {
        if (cityInitialized.current || !initialCityName || !trip.cities) return;
        cityInitialized.current = true;
        const city = trip.cities.find(c => c.name === initialCityName);
        if (city) setSelectedCity(city);
    }, [trip, initialCityName]);

    // Browser back/forward: aggiorna selectedCity in base all'URL
    useEffect(() => {
        const onPop = () => {
            const parts = window.location.pathname.split('/').filter(Boolean);
            if (parts[2] === 'city' && parts[3]) {
                const name = decodeURIComponent(parts[3]);
                setSelectedCity(trip.cities?.find(c => c.name === name) ?? null);
            } else {
                setSelectedCity(null);
            }
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [trip]);

    const [selectedZone, setSelectedZone] = useState({
        hotels: null, restaurants: null, attractions: null, excursions: null,
    });
    const [search, setSearch] = useState('');

    const storageKey = useMemo(() => generateStorageKey(trip), [trip]);
    const [completedTodos, setCompletedTodos] = useLocalStorage(storageKey, {});

    const totalCost = useMemo(() => calculateTotalCost(trip), [trip]);
    const tripDays  = useMemo(() => calculateTripDays(trip.startDate, trip.endDate), [trip.startDate, trip.endDate]);

    const searchTrimmed = search.trim().toLowerCase();
    const searchResults = useMemo(() => {
        if (!searchTrimmed) return [];
        const results = [];
        trip.cities?.forEach(city => {
            city.days?.forEach(day => {
                day.activities?.forEach((activity, actIdx) => {
                    const text = [activity.name, activity.notes, activity.from, activity.to]
                        .filter(Boolean).join(' ').toLowerCase();
                    if (text.includes(searchTrimmed)) {
                        results.push({ city: city.name, date: day.date, activity, actIdx });
                    }
                });
            });
        });
        return results;
    }, [searchTrimmed, trip]);

    const toggleTodo = (key, val) =>
        setCompletedTodos(prev => ({ ...prev, [key]: typeof val === 'boolean' ? val : !prev[key] }));

    const handleZoneSelect = (cat, zone) =>
        setSelectedZone(prev => ({ ...prev, [cat]: prev[cat] === zone ? null : zone }));

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        setSelectedCity(null);
        setSearch('');
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col gap-6">
            <div className="flex items-center gap-3 no-print">
                <button onClick={onBack} className="btn-outline text-sm">
                    {t('tripOverview.backToTrips')}
                </button>
                <button onClick={() => window.print()} className="btn-outline text-sm ml-auto">
                    {t('tripOverview.print')}
                </button>
            </div>

            <TripHeader trip={trip} tripDays={tripDays} totalCost={totalCost} />

            {/* Search bar */}
            <div className="no-print">
                <input
                    type="text"
                    placeholder={t('tripOverview.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="input"
                />
            </div>

            {/* Search results */}
            {searchTrimmed && (
                <div className="card p-6 flex flex-col gap-3">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                        {t('tripOverview.searchResults', { count: searchResults.length, query: search.trim() })}
                    </p>
                    {searchResults.length === 0 && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">{t('tripOverview.noResults')}</p>
                    )}
                    {searchResults.map((r, i) => (
                        <div key={i} className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold text-primary-700 dark:text-primary-400">{r.city}</span>
                                <span className="text-xs text-gray-400">·</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(r.date).toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">{r.activity.name}</p>
                            {r.activity.notes && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{r.activity.notes}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Tab bar */}
            <div className="card overflow-hidden">
                <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto no-print">
                    <div className="flex min-w-max">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.id)}
                                className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-all duration-200 ${
                                    activeTab === tab.id
                                        ? 'border-primary-600 text-primary-700 dark:text-primary-400 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-6">

                    {/* ── Timeline ── */}
                    {activeTab === 'timeline' && (
                        <div className="flex flex-col gap-6">
                            {!selectedCity ? (
                                <>
                                    <h2 className="section-title">{t('tripOverview.selectCity')}</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {trip.cities.map(cityData => (
                                            <CityCard
                                                key={cityData.name}
                                                name={cityData.name}
                                                days={cityData.days}
                                                startDate={cityData.days[0].date}
                                                endDate={cityData.days[cityData.days.length - 1].date}
                                                hotel={cityData.hotel}
                                                onClick={() => {
                                                    setSelectedCity(cityData);
                                                    window.history.pushState(null, '', `/trip/${trip.id}/city/${encodeURIComponent(cityData.name)}`);
                                                }}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <CityDetails trip={trip} city={selectedCity} token={token} onBack={() => {
                                                setSelectedCity(null);
                                                window.history.pushState(null, '', `/trip/${trip.id}`);
                                            }} />
                            )}
                        </div>
                    )}

                    {/* ── Luoghi ── */}
                    {activeTab === 'details' && (
                        <div className="flex flex-col gap-8">
                            <PlacesSection
                                places={trip.places?.hotels} category="hotels" title={t('tripOverview.places.hotels')}
                                buildLinks={p => [
                                    p.bookingLink && { label: t('tripOverview.places.booking'), url: p.bookingLink },
                                    p.mapsLink    && { label: t('tripOverview.places.maps'),    url: p.mapsLink },
                                ].filter(Boolean)}
                                selectedZone={selectedZone} onZoneSelect={handleZoneSelect}
                                priceFormat="pernight"
                            />
                            <PlacesSection
                                places={trip.places?.restaurants} category="restaurants" title={t('tripOverview.places.restaurants')}
                                buildLinks={p => [
                                    p.link     && { label: t('tripOverview.places.website'), url: p.link },
                                    p.mapsLink && { label: t('tripOverview.places.maps'),     url: p.mapsLink },
                                ].filter(Boolean)}
                                selectedZone={selectedZone} onZoneSelect={handleZoneSelect}
                            />
                            <PlacesSection
                                places={trip.places?.attractions} category="attractions" title={t('tripOverview.places.attractions')}
                                buildLinks={p => [
                                    p.link     && { label: t('tripOverview.places.website'), url: p.link },
                                    p.mapsLink && { label: t('tripOverview.places.maps'),     url: p.mapsLink },
                                ].filter(Boolean)}
                                selectedZone={selectedZone} onZoneSelect={handleZoneSelect}
                            />
                            <PlacesSection
                                places={trip.places?.excursions} category="excursions" title={t('tripOverview.places.excursions')}
                                buildLinks={p => [
                                    p.link     && { label: t('tripOverview.places.website'), url: p.link },
                                    p.mapsLink && { label: t('tripOverview.places.maps'),     url: p.mapsLink },
                                ].filter(Boolean)}
                                selectedZone={selectedZone} onZoneSelect={handleZoneSelect}
                            />
                        </div>
                    )}

                    {/* ── Mappa ── */}
                    {activeTab === 'map' && (
                        <div className="h-[500px] rounded-xl overflow-hidden">
                            <MapContainer center={trip?.mapData?.center} zoom={12} style={{ height: '100%', width: '100%' }}>
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />
                                {trip?.mapData?.markers?.map((m, i) => (
                                    <Marker key={i} position={m.position}><Popup>{m.label}</Popup></Marker>
                                ))}
                                {trip?.mapData?.routes?.map((r, i) => (
                                    <Polyline key={i} positions={r} color="#9333ea" />
                                ))}
                            </MapContainer>
                        </div>
                    )}

                    {/* ── Checklist ── */}
                    {activeTab === 'checklist' && (
                        <TodoGrid
                            title={t('tripOverview.checklist.title')}
                            todos={trip.todos}
                            completedTodos={completedTodos}
                            onToggleTodo={toggleTodo}
                        />
                    )}

                    {/* ── Link utili ── */}
                    {activeTab === 'links' && (
                        <LinkGrid title={t('tripOverview.links.title')} links={trip.utilsLinks} />
                    )}
                </div>
            </div>
        </div>
    );
}
